import { Box, Divider } from '@material-ui/core';
import {
  Currency,
  formatMoney,
  LoanStatusExternal,
  MetricAgentView,
  MetricAggregateView,
} from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { ScoreCard } from '@/components/shared/components/ScoreCard';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

import { HorizontalScoreCard } from '../../shared/components/HorizontalScoreCard';
import { MetricVariation } from '../../shared/components/MetricVariation';
import { TimeInterval } from '../../shared/entities/TimeInterval';
import { ScoreCardMetricKey } from '../../shared/types/ScoreCardMetricKey';
import { MetricAnalyzer } from '../entities/MetricAnalyzer';

interface LoanStatusExternalScoreCardProps {
  currency: Currency;
  timeInterval: TimeInterval;
  loanStatusExternal: LoanStatusExternal;
  filteredMetrics?: MetricAggregateView[] | MetricAgentView[];
  selectedMetricsType?: ScoreCardMetricKey;
}

export const LoanStatusExternalScoreCard = ({
  currency,
  timeInterval,
  loanStatusExternal,
  filteredMetrics,
  selectedMetricsType = ScoreCardMetricKey.totalPrincipalAmount,
}: LoanStatusExternalScoreCardProps) => {
  const { md } = useScreen();
  const { customBorderRadius } = useCustomTheme();

  const metricsAnalyzer = useMemo(() => new MetricAnalyzer(timeInterval, filteredMetrics), [filteredMetrics]);

  const { totalPrincipalAmount, totalLoansNumber } = metricsAnalyzer.scoreCardMetrics(loanStatusExternal);

  const showTotalPrincipalAmount = selectedMetricsType === ScoreCardMetricKey.totalPrincipalAmount;

  const selectedCardCommonStyle = {
    transition: 'all ease-in 0.2s',
  };

  if (!md) {
    return (
      <HorizontalScoreCard
        hideDivider
        title={<Text tId={`LOAN_STATUS_EXTERNAL_${loanStatusExternal}`} />}
        subtitle={<Text tId={timeInterval.type} />}
        value={
          showTotalPrincipalAmount
            ? formatMoney({ value: totalPrincipalAmount.current, currency }, 0)
            : totalLoansNumber.current
        }
        subValue={<MetricVariation variation={totalPrincipalAmount.variation} />}
        style={selectedCardCommonStyle}
      />
    );
  }

  return (
    <Box className="appear-anim" display={'flex'}>
      <Box pr={1}>
        <Divider light orientation={'vertical'} />
      </Box>
      <Box
        flexGrow={1}
        style={{
          ...selectedCardCommonStyle,
          padding: '8px 0px 8px 12px',
          borderRadius: customBorderRadius.paper,
        }}
      >
        <ScoreCard
          title={<Text tId={`LOAN_STATUS_EXTERNAL_${loanStatusExternal}`} />}
          subtitle={<Text tId={timeInterval.type} />}
          value={
            showTotalPrincipalAmount
              ? formatMoney({ value: totalPrincipalAmount.current, currency }, 0)
              : totalLoansNumber.current
          }
          subValue={
            <Box minHeight={44}>
              <MetricVariation
                variation={showTotalPrincipalAmount ? totalPrincipalAmount.variation : totalLoansNumber.variation}
                previous={
                  showTotalPrincipalAmount
                    ? totalPrincipalAmount.previous
                      ? formatMoney({ value: totalPrincipalAmount.previous, currency }, 0)
                      : undefined
                    : totalLoansNumber.previous
                    ? totalLoansNumber.previous
                    : undefined
                }
              />
            </Box>
          }
        />
      </Box>
    </Box>
  );
};
